/**
 * useHasPermission — hook + provider per il RBAC dinamico (2026-05-29).
 *
 * Carica una volta al mount le permission keys dell'utente loggato da
 * `/api/permissions/me`, le memorizza in un Set per O(1) lookup, e
 * espone:
 *   const { has, isAdmin, loading } = usePermissions();
 *   has('inventory.item.create') // → true/false
 *
 * NB: l'OPS è admin assoluto → `isAdmin === true` e `has(anything)`
 * ritorna sempre `true` (l'API stessa marca `is_admin`).
 *
 * Strategia caching: dopo il primo fetch, le keys restano in memoria
 * fino a logout/reload. Una mutazione del catalogo (PUT su
 * /permissions/matrix) NON viene auto-rifletta sui client già loggati
 * fino al successivo refresh (~60s di cache backend + reload manuale).
 * Accettabile per la natura "configurativa" del modulo (raro update).
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';
import { useAuth } from './auth';


const PermissionsContext = createContext({
  has: () => false,
  isAdmin: false,
  loading: true,
  refresh: () => Promise.resolve(),
});


export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    keys: new Set(),
    isAdmin: false,
    loading: true,
  });

  const load = useCallback(async () => {
    if (!user) {
      setState({ keys: new Set(), isAdmin: false, loading: false });
      return;
    }
    try {
      const res = await api.get('/permissions/me');
      setState({
        keys: new Set(res.data.permissions || []),
        isAdmin: !!res.data.is_admin,
        loading: false,
      });
    } catch (e) {
      // Fail-closed: in caso di errore l'utente non vede nulla di gate-d.
      setState({ keys: new Set(), isAdmin: false, loading: false });
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const has = useCallback(
    (key) => state.isAdmin || state.keys.has(key),
    [state]
  );

  const value = {
    has,
    isAdmin: state.isAdmin,
    loading: state.loading,
    refresh: load,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}


/** Hook per leggere lo stato dei permessi. */
export function usePermissions() {
  return useContext(PermissionsContext);
}


/** Helper inline per singolo check senza destrutturazione. */
export function useHasPermission(key) {
  return usePermissions().has(key);
}


/**
 * <Can permission="inventory.item.create"> ... </Can>
 *
 * Renderizza i children solo se il permesso è attivo.
 * Utile per nascondere bottoni / sezioni intere.
 */
export function Can({ permission, anyOf, fallback = null, children }) {
  const { has, loading } = usePermissions();
  if (loading) return null;
  const allowed = anyOf
    ? anyOf.some(k => has(k))
    : has(permission);
  return allowed ? children : fallback;
}
